/**
 * `<Modal>` — global modal primitive driven by `useModalStore`.
 *
 * Spec: design/post-walkthrough/global-patterns-spec.md §1.4.
 * Phase 1 (post-walkthrough) changes:
 *   - Backdrop: bg-surface-overlay backdrop-blur-sm (was bg-black/50).
 *   - Container: bg-surface-elevated border-border-default (was hard-coded hex).
 *   - Default focus: Cancel / safe action (NOT the destructive confirm).
 *   - Keyboard: Esc → cancel; Enter → activates focused button.
 *   - Click outside backdrop → cancel.
 *   - confirmCopy prop: when present, gates Confirm behind type-to-match input.
 *
 * Legal-reaccept types ("terms-reaccept" / "privacy-reaccept") are blocking: no Esc,
 * no backdrop-click dismiss, and no Decline button — only Accept (which posts to
 * /auth/accept-legal) closes the modal.
 *
 * Note: JdDeleteConfirmModal.tsx is a local non-store modal; left alone per Phase 1 scope.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useModalStore, type ModalType, type ModalResult } from "@/stores/useModalStore";
import { Button } from "@/components/common/Button";

export function Modal(): JSX.Element {
  const current = useModalStore((state) => state.current);
  const resolve = useModalStore((state) => state.resolve);

  // Derive currentType early so callbacks can reference it in their deps.
  const currentType: ModalType | null = current?.type ?? null;
  const isLegalReaccept =
    currentType === "terms-reaccept" || currentType === "privacy-reaccept";

  // Type-to-confirm state (only active when confirmCopy is set).
  const [confirmInput, setConfirmInput] = useState("");
  // Panel ref used to query-select the safe button for auto-focus.
  const panelRef = useRef<HTMLDivElement>(null);

  // Reset type-to-confirm input whenever modal opens/closes.
  useEffect(() => {
    setConfirmInput("");
  }, [current?.type]);

  // Auto-focus the safe action (Cancel/Decline) on mount. Queried via
  // data-modal-safe attribute to avoid forwardRef on Button.
  useEffect(() => {
    if (current) {
      const t = setTimeout(() => {
        const safeBtn = panelRef.current?.querySelector<HTMLButtonElement>(
          "[data-modal-safe]",
        );
        safeBtn?.focus();
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [current]);

  const handleBackdropClick = useCallback((): void => {
    // Legal-reaccept modals are blocking — backdrop click must not dismiss.
    if (isLegalReaccept) {
      return;
    }
    resolve("dismissed" satisfies ModalResult);
  }, [resolve, isLegalReaccept]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        // Legal-reaccept modals are blocking — Esc must not dismiss.
        if (isLegalReaccept) {
          return;
        }
        resolve("dismissed" satisfies ModalResult);
      }
      // Enter is handled natively by the focused button — no override needed.
    },
    [resolve, isLegalReaccept],
  );

  if (!current) return <></>;

  // currentType is guaranteed non-null here (current is non-null); cast for JSX below.
  const type: ModalType = current.type;
  const confirmCopy = current.props.confirmCopy as string | undefined;
  const needsTypeConfirm = typeof confirmCopy === "string" && confirmCopy.length > 0;
  const typeConfirmMatches = needsTypeConfirm ? confirmInput === confirmCopy : true;

  const title =
    type === "terms-reaccept"
      ? "Terms of Service Updated"
      : type === "privacy-reaccept"
        ? "Privacy Policy Updated"
        : "Confirm";

  const description =
    type === "terms-reaccept"
      ? "Our Terms of Service updated. Review and accept to continue."
      : type === "privacy-reaccept"
        ? "Our Privacy Policy updated. Review and accept to continue."
        : "Are you sure?";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      onKeyDown={handleKeyDown}
      onClick={handleBackdropClick}
    >
      {/* Panel — stop click propagation so backdrop click doesn't dismiss. */}
      <div
        ref={panelRef}
        className="w-full max-w-md rounded-xl border border-border-default bg-surface-elevated p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="modal-title"
          className="mb-2 text-lg font-semibold text-text-emphasis"
        >
          {title}
        </h2>
        <p
          id="modal-description"
          className={`text-sm text-text-secondary ${isLegalReaccept ? "mb-3" : "mb-6"}`}
        >
          {description}
        </p>

        {/* Legal-reaccept: give the user an actual link to READ the updated
            document (opens in a new tab so the modal stays put) — the copy
            says "Review and accept" but there was previously no way to review. */}
        {isLegalReaccept && (
          <p className="mb-6 text-sm">
            <a
              href={type === "terms-reaccept" ? "/legal/terms" : "/legal/privacy"}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-text-link underline hover:text-text-link-hover"
            >
              Review the updated{" "}
              {type === "terms-reaccept" ? "Terms of Service" : "Privacy Policy"}
            </a>
          </p>
        )}

        {/* Type-to-confirm input for high-blast-radius destructive actions. */}
        {needsTypeConfirm && (
          <div className="mb-4">
            <label
              htmlFor="modal-confirm-input"
              className="mb-1.5 block text-xs font-medium text-text-secondary"
            >
              Type{" "}
              <span className="font-semibold text-text-primary">
                {confirmCopy}
              </span>{" "}
              to confirm
            </label>
            <input
              id="modal-confirm-input"
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={confirmCopy}
              className="w-full rounded-md border border-border-strong bg-surface-subtle px-3 py-2 text-sm text-text-primary placeholder-text-tertiary focus:border-[var(--border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--border-focus)]"
              data-testid="modal-confirm-input"
            />
          </div>
        )}

        <div className="flex justify-end gap-3">
          {isLegalReaccept ? (
            <>
              {/*
               * Legal-reaccept: blocking — no Decline, no dismiss.
               * Accept is the only exit; apiClient posts to /auth/accept-legal before resolving.
               * data-modal-safe focuses the Accept button (only button present).
               */}
              <Button
                data-modal-safe
                onClick={() => resolve("confirmed" satisfies ModalResult)}
                disabled={!typeConfirmMatches}
              >
                Accept
              </Button>
            </>
          ) : (
            <>
              {/* data-modal-safe marks the safe action for auto-focus. */}
              <Button
                data-modal-safe
                variant="secondary"
                onClick={() => resolve("dismissed" satisfies ModalResult)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => resolve("confirmed" satisfies ModalResult)}
                disabled={!typeConfirmMatches}
              >
                Confirm
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
